import type { ReactNode } from "react";

interface ProductBlockProps {
  id: string;
  index: string;
  title: string;
  lead: string;
  body: string;
  bullets: string[];
  image: string;
  imageAlt: string;
  reversed?: boolean;
  children?: ReactNode;
}

export default function ProductBlock({
  id,
  index,
  title,
  lead,
  body,
  bullets,
  image,
  imageAlt,
  reversed = false,
  children,
}: ProductBlockProps) {
  return (
    <section id={id} className="border-b border-foreground-200 py-14 md:py-20">
      <div className="grid grid-cols-1 gap-9 lg:grid-cols-12 lg:gap-14">
        <div className={`lg:col-span-5 ${reversed ? "lg:order-2" : ""}`}>
          <div className="relative">
            <div className="absolute -left-2.5 -top-2.5 h-12 w-12 border-l-2 border-t-2 border-primary-500" />
            <div className="relative h-[250px] w-full overflow-hidden border border-foreground-200 bg-secondary-100 sm:h-[320px] lg:h-[400px]">
              <img
                src={image}
                alt={imageAlt}
                width={700}
                height={800}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute bottom-3 left-3 flex h-11 w-11 items-center justify-center bg-foreground-950 font-label text-[12px] tracking-[0.1em] text-background-100">
              {index}
            </div>
          </div>
        </div>

        <div className={`lg:col-span-7 ${reversed ? "lg:order-1" : ""}`}>
          <h2 className="font-heading text-[26px] font-extrabold leading-[1.1] tracking-tightest text-foreground-950 sm:text-[32px] md:text-[38px]">
            {title}
          </h2>
          <p className="mt-4 max-w-2xl text-[15px] font-medium leading-relaxed text-foreground-700 md:text-[16.5px]">
            {lead}
          </p>
          <p className="mt-4 max-w-2xl text-[13.5px] leading-relaxed text-foreground-600 md:text-[14.5px]">
            {body}
          </p>

          <ul className="mt-7 grid grid-cols-1 gap-x-8 sm:grid-cols-2">
            {bullets.map((bullet) => (
              <li
                key={bullet}
                className="flex items-center gap-2.5 border-b border-dashed border-foreground-200 py-2.5 font-label text-[11.5px] uppercase tracking-[0.04em] text-foreground-700"
              >
                <i className="ri-checkbox-blank-circle-fill flex-none text-[4px] text-primary-500" />
                {bullet}
              </li>
            ))}
          </ul>

          {children}
        </div>
      </div>
    </section>
  );
}